import { useNavigate } from '@tanstack/react-router'
import { ArrowLeft, FileDown, History } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { HomeContentContainer } from '@/components'
import { toast } from '@/components/common/Toast'
import { Button, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useInitialLocationFilter } from '@/hooks/useInitialLocationFilter'
import { BILL_LOCATION_LABELS, BILL_LOCATION_VALUES, type BillLocationValue } from '@/constants/bill-location'
import { useLanguage } from '@/hooks'
import { useTranslations } from '@/hooks/useTranslations'
import { useGetCustomerNoPage } from '@/modules/customer/services/customer'
import {
  printBulkPaymentPdf,
  useGetBulkPaymentBatch,
  useGetBulkPaymentHistory,
} from '@/modules/bill/services/bill'
import * as DateUtil from '@/utils/date'
import { formatNumber } from '@/utils/number'

import { AllocationPreviewTable } from './AllocationPreviewTable'

export function BulkPaymentHistoryPage() {
  const t = useTranslations('admin.outstanding_management.bulk_history')
  const navigate = useNavigate()
  const { locale } = useLanguage()

  const [page, setPage] = useState(1)
  const [locationFilter, setLocationFilter] = useInitialLocationFilter()
  const [billTypeFilter, setBillTypeFilter] = useState<'all' | 'buy' | 'sell'>('all')
  const [customerFilter, setCustomerFilter] = useState<string>('all')
  const [detailBatchId, setDetailBatchId] = useState<string | undefined>(undefined)

  const { data: customerList } = useGetCustomerNoPage()
  const { data, isPending } = useGetBulkPaymentHistory({
    page,
    pageSize: 20,
    customer_id: customerFilter === 'all' ? undefined : customerFilter,
    location: locationFilter || undefined,
    bill_type: billTypeFilter === 'all' ? undefined : billTypeFilter,
  })

  const {
    data: detailData,
    isPending: isDetailPending,
    isError: isDetailError,
    error: detailError,
  } = useGetBulkPaymentBatch(detailBatchId)

  useEffect(() => {
    if (!isDetailError || !detailError) return
    const message =
      detailError instanceof Error
        ? detailError.message
        : 'ไม่สามารถโหลดรายละเอียดได้'
    toast.toastFailed(message)
  }, [isDetailError, detailError])

  const rows = data?.data ?? []
  const meta = data?.meta
  const customers = customerList?.data ?? []

  const paymentMethodLabel = useMemo(
    () =>
      ({
        cash: t('cash'),
        transfer: t('transfer'),
      }) as Record<string, string>,
    [t],
  )

  const handlePrintPdf = async (batchId: string) => {
    try {
      await printBulkPaymentPdf(batchId)
    } catch (e) {
      if (e instanceof Error) toast.toastFailed(e.message)
    }
  }

  return (
    <HomeContentContainer title={t('title')} subtitle={t('subtitle')}>
      <div className="mb-4 flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={() => navigate({ to: '/admin/outstanding' })}>
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
          {t('back')}
        </Button>
        <Button type="button" variant="default" onClick={() => navigate({ to: '/admin/outstanding/settle' })}>
          <History className="mr-2 h-4 w-4" aria-hidden />
          {t('new_settle')}
        </Button>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">{t('filters')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <div className="grid gap-2">
            <Label>{t('location')}</Label>
            <Select
              value={locationFilter || 'all'}
              onValueChange={(v) => {
                setLocationFilter(v === 'all' ? '' : (v as BillLocationValue))
                setPage(1)
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('all')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                {BILL_LOCATION_VALUES.map((loc) => (
                  <SelectItem key={loc} value={loc}>
                    {BILL_LOCATION_LABELS[loc]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>{t('bill_type')}</Label>
            <Select
              value={billTypeFilter}
              onValueChange={(v) => {
                setBillTypeFilter(v as 'all' | 'buy' | 'sell')
                setPage(1)
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="buy">ซื้อ</SelectItem>
                <SelectItem value="sell">ขาย</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>{t('customer')}</Label>
            <Select
              value={customerFilter}
              onValueChange={(v) => {
                setCustomerFilter(v)
                setPage(1)
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('all')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          {isPending ? (
            <p className="text-sm text-muted-foreground">{t('loading')}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('empty')}</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('col_paid_at')}</TableHead>
                    <TableHead>{t('col_customer')}</TableHead>
                    <TableHead>{t('location')}</TableHead>
                    <TableHead>{t('bill_type')}</TableHead>
                    <TableHead className="text-right">{t('col_amount')}</TableHead>
                    <TableHead className="text-right">{t('col_bills')}</TableHead>
                    <TableHead>{t('col_by')}</TableHead>
                    <TableHead className="text-right">{t('col_actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        {DateUtil.formatDate(row.paid_at, DateUtil.DateFormat.DATE_TIME, locale)}
                      </TableCell>
                      <TableCell>{row.customer_name}</TableCell>
                      <TableCell>
                        {BILL_LOCATION_LABELS[row.location as BillLocationValue] ?? row.location}
                      </TableCell>
                      <TableCell>{row.bill_type === 'sell' ? 'ขาย' : 'ซื้อ'}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatNumber(row.paid_amount)}
                      </TableCell>
                      <TableCell className="text-right">{row.bill_count}</TableCell>
                      <TableCell className="text-muted-foreground">{row.created_by_name ?? '—'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setDetailBatchId(row.id)}
                          >
                            {t('detail')}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="default"
                            onClick={() => void handlePrintPdf(row.id)}
                          >
                            <FileDown className="mr-1 h-3.5 w-3.5" aria-hidden />
                            {t('pdf')}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {meta && meta.totalPages > 1 ? (
            <div className="mt-4 flex items-center justify-between gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t('prev_page')}
              </Button>
              <span className="text-sm text-muted-foreground">
                {t('page_info', { page: meta.currentPage, total: meta.totalPages })}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('next_page')}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Dialog open={!!detailBatchId} onOpenChange={(open) => !open && setDetailBatchId(undefined)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('detail_title')}</DialogTitle>
          </DialogHeader>
          {isDetailPending ? (
            <p className="text-sm text-muted-foreground">{t('loading')}</p>
          ) : isDetailError ? (
            <p className="text-sm text-destructive">{t('detail_error')}</p>
          ) : detailData?.data ? (
            <div className="space-y-4">
              <div className="grid gap-1 text-sm">
                <p>
                  <span className="text-muted-foreground">{t('col_customer')}: </span>
                  {detailData.data.customer_name}
                </p>
                <p>
                  <span className="text-muted-foreground">{t('location')}: </span>
                  {BILL_LOCATION_LABELS[detailData.data.location as BillLocationValue] ??
                    detailData.data.location}
                </p>
                <p>
                  <span className="text-muted-foreground">{t('col_amount')}: </span>
                  {formatNumber(detailData.data.paid_amount)} บาท (
                  {paymentMethodLabel[detailData.data.payment_method] ?? detailData.data.payment_method})
                </p>
              </div>
              <AllocationPreviewTable allocations={detailData.data.allocations} />
              <Button
                type="button"
                className="w-full sm:w-auto"
                onClick={() => detailBatchId && void handlePrintPdf(detailBatchId)}
              >
                <FileDown className="mr-2 h-4 w-4" aria-hidden />
                {t('pdf')}
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </HomeContentContainer>
  )
}
